import { useState } from 'react';
import { ShoppingCart, Check, Package, Layers, Sparkles, ShieldCheck, AlertTriangle, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button.jsx';
import { formatCurrency, cn } from '../../lib/utils.js';

export const ROLE_LABELS = {
  main_panel: 'Zentrale / BMZ',
  expansion_module: 'Erweiterungsmodul',
  detector: 'Melder / Sensor',
  power_supply: 'Energieversorgung',
  signaling: 'Signalgeber',
  accessory: 'Zubehör',
};

export const ROLE_COLORS = {
  main_panel: 'bg-red-50 text-red-700 border-red-200',
  expansion_module: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  detector: 'bg-amber-50 text-amber-700 border-amber-200',
  power_supply: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  signaling: 'bg-blue-50 text-blue-700 border-blue-200',
  accessory: 'bg-slate-50 text-slate-700 border-slate-200',
};

/**
 * Interactive Bill of Materials (BoM) Card
 * Renders verified hardware lists and typed SystemPlanningBomPayload generated by ALWIS with 1-click import to inquiry cart.
 */
export function BomCard({
  bom,
  onImportBom,
  isImporting: controlledImporting = false,
  imported: controlledImported = false,
  className = '',
}) {
  const [localImported, setLocalImported] = useState(controlledImported);
  const [localImporting, setLocalImporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  if (!bom) return null;

  // Support both typed payload { title, project_title, system_type, planning_standard, applicable_norms, items, ... } and raw items array
  const isPayloadObject = !Array.isArray(bom) && typeof bom === 'object';
  const items = Array.isArray(bom) ? bom : Array.isArray(bom.items) ? bom.items : [];
  const title = (isPayloadObject && (bom.title || bom.project_title)) ? (bom.title || bom.project_title) : 'Stückliste / Hardware-BoM';
  const systemType = isPayloadObject ? (bom.system_type || bom.systemType) : null;
  const planningStandard = isPayloadObject ? (bom.planning_standard || bom.planningStandard) : null;
  const applicableNorms = isPayloadObject && Array.isArray(bom.applicable_norms) ? bom.applicable_norms : (isPayloadObject && Array.isArray(bom.applicableNorms) ? bom.applicableNorms : []);
  const warnings = isPayloadObject && Array.isArray(bom.warnings) ? bom.warnings : [];
  const notes = isPayloadObject ? (bom.notes || bom.norm_citation_summary) : null;

  // Calculate or retrieve subtotal
  const computedSubtotal = items.reduce((acc, item) => {
    const qty = Math.max(1, Number(item.quantity) || 1);
    const unitPrice = Math.max(0, Number(item.unitPriceCents ?? item.unit_price_cents ?? item.unit_price_net_cents ?? item.price?.cents ?? 0));
    return acc + (qty * unitPrice);
  }, 0);

  const subtotalCents = (isPayloadObject && (bom.subtotalCents ?? bom.subtotal_cents ?? bom.totalEstimatedNetCents ?? bom.total_estimated_net_cents)) !== undefined
    ? Number(bom.subtotalCents ?? bom.subtotal_cents ?? bom.totalEstimatedNetCents ?? bom.total_estimated_net_cents)
    : computedSubtotal;

  const totalQuantity = items.reduce((acc, it) => acc + Math.max(1, Number(it.quantity) || 1), 0);
  const effectiveImported = controlledImported || localImported;
  const effectiveImporting = controlledImporting || localImporting;

  const handleImport = async () => {
    if (effectiveImporting || effectiveImported || items.length === 0) return;
    setErrorMessage(null);

    const idempotencyKey =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `bom_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    if (onImportBom) {
      setLocalImporting(true);
      try {
        const result = onImportBom(bom, idempotencyKey);
        if (result && typeof result.then === 'function') {
          await result;
        }
        setLocalImported(true);
      } catch (err) {
        setErrorMessage(err?.message || 'Fehler beim Übernehmen der Stückliste.');
      } finally {
        setLocalImporting(false);
      }
    } else {
      setLocalImported(true);
    }
  };

  return (
    <div
      data-testid="bom-card"
      data-system-type={systemType || undefined}
      className={cn(
        'my-3 overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] shadow-xs transition-all',
        className
      )}
    >
      {/* BoM Card Header */}
      <div className="border-b border-[var(--border)] bg-white px-4 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--primary)]/10 text-[var(--primary)] shrink-0">
              <Layers className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h4 className="text-xs font-bold text-[var(--foreground)] truncate font-display">
                {title}
              </h4>
              <div className="flex items-center gap-1.5 flex-wrap text-xs text-[var(--secondary)]">
                <span>{`${items.length} Position${items.length !== 1 ? 'en' : ''} (${totalQuantity} Stück gesamt)`}</span>
                {systemType && (
                  <>
                    <span>&bull;</span>
                    <span className="font-semibold text-[var(--foreground)] uppercase font-mono">{systemType}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-xs font-bold text-[var(--primary)] shrink-0">
            <Sparkles className="h-3 w-3" />
            ALWIS Verifiziert
          </span>
        </div>

        {/* Norm Standards & Applicable Citations Strip */}
        {(planningStandard || applicableNorms.length > 0) && (
          <div className="mt-2 pt-2 border-t border-[var(--border)] flex items-center gap-1.5 flex-wrap text-xs">
            <span className="font-medium text-[var(--secondary)] flex items-center gap-1">
              <ShieldCheck className="h-3 w-3 text-emerald-600 shrink-0" />
              Normen:
            </span>
            {planningStandard && (
              <span
                data-testid="planning-standard-pill"
                className="px-1.5 py-0.2 rounded bg-slate-100 border border-slate-200 text-slate-800 font-mono font-medium"
              >
                {planningStandard}
              </span>
            )}
            {applicableNorms.map((norm, nIdx) => (
              <span
                key={`norm-${nIdx}`}
                data-testid="applicable-norm-pill"
                className="px-1.5 py-0.2 rounded bg-slate-100 border border-slate-200 text-slate-800 font-mono"
              >
                {norm}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Warnings Banner if any */}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800 space-y-1">
          {warnings.map((w, wIdx) => (
            <div key={`warn-${wIdx}`} className="flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Error Alert if import failed */}
      {errorMessage && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-xs text-red-800 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <AlertCircle className="h-3.5 w-3.5 text-red-600 shrink-0" />
            <span className="truncate">{errorMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setErrorMessage(null)}
            className="text-xs font-semibold text-red-700 hover:underline shrink-0"
          >
            Ausblenden
          </button>
        </div>
      )}

      {/* Items List */}
      <div className="divide-y divide-[var(--border)] px-4 py-1">
        {items.map((item, idx) => {
          const sku = item.sku || item.productId || item.product_id || item.id || `pos-${idx + 1}`;
          const qty = Math.max(1, Number(item.quantity) || 1);
          const unitPrice = Math.max(0, Number(item.unitPriceCents ?? item.unit_price_cents ?? item.unit_price_net_cents ?? item.price?.cents ?? 0));
          const lineTotal = unitPrice * qty;
          const role = item.role;
          const roleLabel = role ? (ROLE_LABELS[role] || role) : null;
          const roleColorClass = role ? (ROLE_COLORS[role] || 'bg-slate-50 text-slate-700 border-slate-200') : '';

          const normCitation = item.norm_citation || item.normCitation || item.norm_justification || (Array.isArray(item.standardReferences) && item.standardReferences.length > 0 ? item.standardReferences.join(', ') : null);

          return (
            <div
              key={`${sku}-${idx}`}
              data-testid="bom-line-item"
              data-sku={sku}
              className="flex items-start justify-between gap-3 py-2.5 text-xs"
            >
              <div className="flex items-start gap-2 min-w-0 flex-1">
                <div className="flex h-6 w-6 items-center justify-center rounded bg-white border border-[var(--border)] text-xs font-bold text-[var(--foreground)] shrink-0 mt-0.5">
                  {qty}&times;
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-semibold text-[var(--foreground)] truncate leading-tight">
                      {item.name || `Artikel ${sku}`}
                    </p>
                    {role && (
                      <span
                        data-testid="bom-item-role-badge"
                        data-role={role}
                        className={cn(
                          'inline-flex items-center rounded px-1.5 py-0.2 text-xs font-semibold border',
                          roleColorClass
                        )}
                      >
                        {roleLabel}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-xs text-[var(--secondary)] font-mono mt-0.5 flex-wrap">
                    <span className="font-medium">Art.-Nr. {sku}</span>
                    {item.manufacturer && (
                      <>
                        <span>&bull;</span>
                        <span>{item.manufacturer}</span>
                      </>
                    )}
                  </div>

                  {normCitation && (
                    <div
                      data-testid="item-norm-citation"
                      className="mt-1 inline-flex items-center gap-1 rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-xs text-slate-700 font-mono"
                      title={normCitation}
                    >
                      <ShieldCheck className="h-2.5 w-2.5 text-emerald-600 shrink-0" />
                      <span className="truncate max-w-[280px]">{normCitation}</span>
                    </div>
                  )}

                  {item.description && (
                    <p className="text-xs text-[var(--secondary)] mt-0.5 line-clamp-1">
                      {item.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right shrink-0 pt-0.5">
                <p className="font-bold text-[var(--foreground)]">
                  {unitPrice > 0 ? formatCurrency(lineTotal) : 'Auf Anfrage'}
                </p>
                {qty > 1 && unitPrice > 0 && (
                  <p className="text-xs text-[var(--secondary)]">
                    {formatCurrency(unitPrice)} / Stk.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Notes footer if provided */}
      {notes && (
        <div className="px-4 py-1.5 bg-slate-50 border-t border-[var(--border)] text-xs text-[var(--secondary)] italic">
          {notes}
        </div>
      )}

      {/* BoM Card Footer / 1-Click Import Action */}
      <div className="border-t border-[var(--border)] bg-white px-4 py-3">
        <div className="flex items-center justify-between mb-2.5 text-xs">
          <span className="text-[var(--secondary)] font-medium">Geschätzte Zwischensumme (netto):</span>
          <span className="font-bold text-[var(--foreground)] text-sm">
            {subtotalCents > 0 ? formatCurrency(subtotalCents) : 'Auf Anfrage'}
          </span>
        </div>

        <Button
          type="button"
          data-testid="bom-import-btn"
          size="sm"
          onClick={handleImport}
          disabled={effectiveImporting || effectiveImported || items.length === 0}
          className={cn(
            'w-full gap-2 font-bold shadow-xs transition-all cursor-pointer h-9',
            effectiveImported
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-default'
              : 'bg-[var(--primary)] hover:opacity-90 text-[var(--primary-foreground,#ffffff)]'
          )}
        >
          {effectiveImported ? (
            <>
              <Check className="h-4 w-4" />
              <span>Im Anfragekorb übernommen</span>
            </>
          ) : effectiveImporting ? (
            <>
              <Package className="h-4 w-4 animate-spin" />
              <span>Wird übernommen...</span>
            </>
          ) : errorMessage ? (
            <>
              <RefreshCw className="h-4 w-4" />
              <span>Erneut versuchen (1-Click BoM)</span>
            </>
          ) : (
            <>
              <ShoppingCart className="h-4 w-4" />
              <span>In Anfragekorb übernehmen (1-Click BoM)</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

